/**
 * Plattform-Ports ([V] 4.3 `platform.ts`, E-o, E-p19/p20; Anhang A.platform): Registrierung von Queues und
 * Zeitplänen (Eigentum `packages/pipeline`), Demo-Daten, Erinnerungen ([A] 2.3 `reminder`).
 *
 * `ReminderDraft.params` erlaubt nur Zahlen und Zeichenketten (keine Objekte, keine Klartexte verschachtelt).
 */
import type { z } from 'zod';
import type { DbTx, SystemContext, UserId } from './common.js';

export interface QueueRegistry {
  /** Wirft bei doppeltem Namen; Namen in kebab-case (E-p20). */
  register(
    name: string,
    opts: {
      globalConcurrency?: number;
      workerConcurrency?: number;
      partitionConcurrency?: number;
      partitionRateLimit?: { limitPerPeriod: number; periodSec: number };
      rateLimit?: { limitPerPeriod: number; periodSec: number };
    },
  ): void;
}

export interface ScheduleRegistry {
  register(s: {
    name: string;
    cron: string;
    timeZone: 'UTC' | 'Europe/Berlin';
    workflow: string;
  }): void;
}

/** Deterministischer ID-Raum der Demo-Daten eines Demo-Benutzers (E-o). */
export interface DemoIdSpace {
  readonly userId: UserId;
  id(kind: string, n: number): string;
}

export interface DemoSeedContributor {
  module: string;
  order: number;
  seed(tx: DbTx, rng: { next(): number }, ids: DemoIdSpace): Promise<{ rows: number }>;
}

/** CHECK-Liste `reminder.kind` ([A] 2.3); `art14_due` liefert `compliance`, `event_delivery_failed` die Outbox. */
export const REMINDER_KINDS = [
  'token_expiring',
  'token_expired',
  'token_issue_unknown',
  'auth_failed',
  'consent_expiring',
  'consent_revoked',
  'account_mismatch',
  'unbound_token',
  'sync_stale',
  'data_gap',
  'processing_errors',
  'identity_review',
  'self_url_unverified',
  'privacy_request_due',
  'art14_due',
  'erasure_pending',
  'restore_applied',
  'event_delivery_failed',
] as const;

export type ReminderDraft = {
  kind: (typeof REMINDER_KINDS)[number];
  level: 'info' | 'warning' | 'critical';
  dedupeKey: string;
  dueAt: Date;
  connectedAccountId: string | null;
  params: Record<string, string | number>;
};

export interface ReminderSource {
  kind: string;
  compute(tx: DbTx, userId: UserId, now: Date): Promise<ReminderDraft[]>;
}

/**
 * Workflow-Deskriptoren für Domänenpakete ohne `pipeline`-Import (C-W2, Plan Welle 2 C VB-1 c / VB-5, Abschnitt 1.6;
 * gemeinsame Fassung für die Pläne 2-A, 2-B, 2-C und 2-D). `compliance`, `relationship-building`, `targets`,
 * `communication` und `visibility` dürfen `@socialfy/pipeline` (und damit DBOS) nicht importieren; sie beschreiben ihre
 * Abläufe als reine Deskriptoren und bekommen die Laufzeit über `WorkflowEnv` gereicht. W3-03 registriert die
 * Deskriptoren mit `defineWorkflow`/`registerRedactedWorkflow`/`createScheduleRegistry`/`createQueueRegistry` und bindet
 * `StepRunner` an `runRedactedStep`, `WorkflowStarter` an `QueueRegistry.enqueue`.
 *
 * Regeln (Plan 2-C 1.6 R1–R5, hier nur beschrieben; die Prüfung liegt in `pipeline`):
 * - Ein- und Ausgaben und Schrittergebnisse tragen nur IDs, Zeitangaben, Aufzählungen, Zahlen und Wahrheitswerte (nie
 *   freie Zeichenketten); `workflowId` folgt `WORKFLOW_ID_PATTERN` (`pipeline`).
 * - kein `Date.now()`/`Math.random()` im Ablaufkörper; jede fachliche Wirkung liegt in genau einem Schritt.
 * - Fehler eines Schritts sind `{ class, code }` (nie Meldungstext), analog `SanitizedError` in `security`.
 */
export type StepError = { class: string; code: string };
export type StepOutcome<T> = { ok: true; value: T } | { ok: false; error: StepError };

/**
 * Führt `fn` als dauerhaften Schritt aus; Ausnahmen werden **im** Schritt gefangen und als `{ ok: false, error }`
 * geliefert, das Ergebnis wird gegen `output` geprüft. Gleiche Form wie `runRedactedStep` in `pipeline`.
 */
export type StepRunner = <S extends z.ZodType>(
  name: string,
  fn: () => Promise<z.input<S>>,
  output: S,
) => Promise<StepOutcome<z.output<S>>>;

export type WorkflowStartOptions = { workflowId: string; queue: string; partitionKey?: string };

/** Startet einen Ablauf idempotent je `workflowId` (`started: false` = lief bzw. läuft bereits). */
export interface WorkflowStarter {
  start(workflow: string, input: unknown, o: WorkflowStartOptions): Promise<{ started: boolean }>;
}

export type WorkflowStatus = 'unknown' | 'pending' | 'success' | 'error' | 'cancelled';

/** Abbruch und Statusabfrage eines Ablaufs (Plan 2-A: TP2 bricht Recherche und Entwurf beim Löschen ab). */
export interface WorkflowControl {
  cancel(workflowId: string): Promise<void>;
  status(workflowId: string): Promise<WorkflowStatus>;
}

/** Laufzeit eines Ablaufs: dauerhafte Schritte, Kindstarts und der Systemkontext je Benutzer. */
export type WorkflowEnv = {
  step: StepRunner;
  start: WorkflowStarter;
  ctxFor(userId: UserId): SystemContext;
};

export interface DomainWorkflow<I extends z.ZodType, O extends z.ZodType> {
  /** `/^[A-Za-z][A-Za-z0-9_.-]{2,79}$/` */
  name: string;
  /** einer aus `QUEUE_NAMES` (`pipeline`) */
  queue: string;
  input: I;
  output: O;
  steps: Readonly<Record<string, z.ZodType>>;
  workflowId(input: z.output<I>): string;
  partitionKey?(input: z.output<I>): string;
  run(input: z.output<I>, env: WorkflowEnv): Promise<z.input<O>>;
}

export interface DomainSchedule {
  /** kebab-case, 3–41 Zeichen; Zeitzone `Europe/Berlin` nicht zwischen 02:00 und 03:00 ([ST] W0-08). */
  name: string;
  cron: string;
  timeZone: 'UTC' | 'Europe/Berlin';
  queue: string;
  steps: Readonly<Record<string, z.ZodType>>;
  run(scheduledAt: Date, env: WorkflowEnv): Promise<void>;
}
